import React, { useEffect, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import Loading from './Loading';
import SessionInactivityBanner from './SessionInactivityBanner';
import {
  fetchAndCacheModuleAccess,
  clearModuleAccessCache,
  userHasModule,
  normalizeModuleAccess,
  getFirstAllowedHomePathFromModuleAccess
} from '../utils/moduleAccess';
import { requestBrowserNotificationPermissionFromGesture } from '../utils/browserNotifications';
import { registerServiceWorkerForNotifications, supportsServiceWorkerNotifications } from '../utils/webPush';

export default function ModuleRoute({ moduleKey, children, redirectTo = '/' }) {
  const notificationGestureDoneRef = useRef(false);

  const [state, setState] = useState(() => ({
    loading: true,
    module_access: [],
    user: null,
    error: null,
  }));

  useEffect(() => {
    let mounted = true;
    fetchAndCacheModuleAccess({ forceFresh: true })
      .then(({ user, module_access }) => {
        if (!mounted) return;
        setState({ loading: false, module_access, user, error: null });
      })
      .catch((e) => {
        if (!mounted) return;
        const noAuth = e?.message?.includes('UNAUTHORIZED');
        if (noAuth) {
          clearModuleAccessCache();
          setState({ loading: false, module_access: [], user: null, error: e?.message || 'error' });
          return;
        }
        // Si la revalidación falla por red y había estado previo, mantenemos experiencia fluida.
        setState((prev) => ({ ...prev, loading: false, error: e?.message || 'error' }));
      });
    return () => { mounted = false; };
  }, []);

  /** Si el permiso ya estaba en "granted", registrar el SW local para notificaciones. */
  useEffect(() => {
    if (state.loading) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (!supportsServiceWorkerNotifications()) return;
    registerServiceWorkerForNotifications().catch(() => {});
  }, [state.loading]);

  /**
   * Muchos navegadores solo conceden notificaciones del sistema tras un gesto del usuario.
   * El primer clic en la app (tras entrar a un módulo) solicita permiso para que avisos
   * de tickets / sesión sigan viéndose con otra ventana enfocada.
   */
  useEffect(() => {
    if (state.loading) return;

    const onFirstClick = () => {
      if (notificationGestureDoneRef.current) return;
      notificationGestureDoneRef.current = true;
      requestBrowserNotificationPermissionFromGesture()
        .then((perm) => {
          if (perm === 'granted' && supportsServiceWorkerNotifications()) {
            registerServiceWorkerForNotifications().catch(() => {});
          }
        })
        .catch(() => {});
      document.removeEventListener('click', onFirstClick, true);
      document.removeEventListener('keydown', onFirstKey, true);
    };
    const onFirstKey = (e) => {
      if (e.key === 'Enter' || e.key === ' ') onFirstClick();
    };
    document.addEventListener('click', onFirstClick, true);
    document.addEventListener('keydown', onFirstKey, true);
    return () => {
      document.removeEventListener('click', onFirstClick, true);
      document.removeEventListener('keydown', onFirstKey, true);
    };
  }, [state.loading]);

  if (state.loading) {
    return (
      <div className="app-route-fallback" role="status" aria-live="polite" aria-label="Cargando permisos">
        <Loading inline />
      </div>
    );
  }

  // Obligatorio cambiar contraseña: enviar a login para mostrar el modal (no permitir usar la app hasta cambiar)
  if (state.user?.must_change_password === true) {
    return <Navigate to="/" replace />;
  }

  // Sin acceso al módulo: ir a otra ruta permitida (no al login, para no “cerrar sesión”)
  const accessForCheck = {
    must_change_password: state.user?.must_change_password,
    module_access: normalizeModuleAccess(state.user?.module_access ?? state.module_access),
  };
  if (!userHasModule(accessForCheck, moduleKey)) {
    const fallback = getFirstAllowedHomePathFromModuleAccess(accessForCheck.module_access);
    return <Navigate to={fallback || redirectTo} replace />;
  }

  return (
    <>
      <SessionInactivityBanner />
      {children}
    </>
  );
}

